// You should not directly use this file. It's the minimal representation of
// a geometry that GridGeometry turns into a full interface. Use GridGeometry.

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const SquareGridGeometryCore = function(cols, rows, options) {
	this.style = "square";
	this.cols = cols;
	this.rows = rows;
	this.options = options || {};
};

SquareGridGeometryCore.prototype.getBaseWidth = function() {
	return this.cols;
};

SquareGridGeometryCore.prototype.getBaseHeight = function() {
	return this.rows;
};

// This should return the cells in left-to-right then top-to-bottom order.
SquareGridGeometryCore.prototype.getAllCells = function() {
	const result = [];
	for (let j = 0; j < this.rows; j++) {
		for (let i = 0; i < this.cols; i++) {
			result.push([i, j]);
		}
	}
	return result;
};

// Converts string representation of z in edge (third coordinate) to real
// value. In square grids it's a boolean representing whether it's horizontal.
SquareGridGeometryCore.prototype.convertEdgeZ = function(isHorizontal) {
	return isHorizontal === "true";
};

// Order matters: the edges must go clockwise around the cell
SquareGridGeometryCore.prototype.getCellEdges = function(x, y) {
	return [
		[x, y, true],
		[x + 1, y, false],
		[x, y + 1, true],
		[x, y, false],
	];
};

SquareGridGeometryCore.prototype.getEdgeCells = function(x, y, isHorizontal) {
	return [
		[x + (isHorizontal ? 0 : -1), y + (isHorizontal ? -1 : 0)],
		[x, y],
	];
};

SquareGridGeometryCore.prototype.getEdgeVertices = function(
	x,
	y,
	isHorizontal
) {
	return [
		[x, y],
		[x + (isHorizontal ? 1 : 0), y + (isHorizontal ? 0 : 1)],
	];
};

// Order matters: the edges must go clockwise around the vertex.
SquareGridGeometryCore.prototype.getVertexEdges = function(x, y) {
	return [
		[x, y - 1, false],
		[x, y, true],
		[x, y, false],
		[x - 1, y, true],
	];
};

SquareGridGeometryCore.prototype.getCollinearEdge = function(edge, vertex) {
	const [ex, ey, horiz] = edge;
	const [vx, vy] = vertex;
	if (ex === vx && ey === vy) {
		return [vx - (horiz ? 1 : 0), vy - (horiz ? 0 : 1), horiz];
	} else if (horiz && ex + 1 === vx && ey === vy) {
		return [vx, vy, true];
	} else if (!horiz && ex === vx && ey + 1 === vy) {
		return [vx, vy, false];
	}
	return null;
};

SquareGridGeometryCore.prototype.getDrawCellRect = function(dim, x, y) {
	return {
		x: dim.x + x * dim.size,
		y: dim.y + y * dim.size,
		w: dim.size,
		h: dim.size,
	};
};

SquareGridGeometryCore.prototype.getDrawCellCenter = function(dim, x, y) {
	const rect = this.getDrawCellRect(dim, x, y);
	return [rect.x + rect.w / 2, rect.y + rect.h / 2];
};

SquareGridGeometryCore.prototype.getDrawVertex = function(dim, x, y) {
	return [dim.x + x * dim.size, dim.y + y * dim.size];
};

SquareGridGeometryCore.prototype.mouseToGrid = function(dim, mouseX, mouseY) {
	return [(mouseX - dim.x) / dim.size, (mouseY - dim.y) / dim.size];
};

SquareGridGeometryCore.prototype.gridToMouse = function(dim, coordX, coordY) {
	return [coordX * dim.size + dim.x, coordY * dim.size + dim.y];
};

// Returns an [x, y] cell close to the passed point. If the closest cell,
// edge, and vertex neighbor the returned cell, that's good enough.
SquareGridGeometryCore.prototype.getMousedCell = function(
	coordX,
	coordY,
	clamp
) {
	return [Math.floor(coordX), Math.floor(coordY)];
};

// Given a dead center distance (0-1) and dim-independent coordinates for the
// mouse and closest cell center, return whether the dead center check prevents
// mousing an edge.
SquareGridGeometryCore.prototype.isDeadCenter = function(
	deadDistance,
	coordX,
	coordY,
	centerX,
	centerY
) {
	return (
		Math.abs(coordX - centerX) < deadDistance / 2 &&
		Math.abs(coordY - centerY) < deadDistance / 2
	);
};

// Given a dead vertex distance (0-1) and dim-independent coordinates for the
// mouse and closest vertex, return whether the dead vertex check prevents
// mousing an edge.
SquareGridGeometryCore.prototype.isDeadVertex = function(
	deadDistance,
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	return (
		Math.abs(coordX - vertexX) < deadDistance / 2 &&
		Math.abs(coordY - vertexY) < deadDistance / 2
	);
};

SquareGridGeometryCore.prototype.isLooseVertexFit = function(
	coordX,
	coordY,
	vertexX,
	vertexY
) {
	const dx = vertexX - coordX;
	const dy = vertexY - coordY;
	const squareDist = dx * dx + dy * dy;
	return squareDist > 0.1;
};

SquareGridGeometryCore.prototype.isNewEdge = function(
	coordX,
	coordY,
	newEdge,
	lastEdge
) {
	const fx = coordX - Math.floor(coordX);
	const fy = coordY - Math.floor(coordY);
	let scalar;
	if (newEdge[2] && lastEdge[2]) {
		// Both horizontal
		return fx >= 0.25 && fx <= 0.75;
	} else if (!newEdge[2] && !lastEdge[2]) {
		// Both vertical
		return fy >= 0.25 && fy <= 0.75;
	} else if (lastEdge[2]) {
		// Perpendicular, horizontal -> vertical
		scalar = newEdge[1] < lastEdge[1] ? 1 : -1; // Heading up (1) or down (-1)
		return scalar * (fy - 0.5) <= 0;
	} else {
		// Perpendicular, vertical -> horizontal
		scalar = newEdge[0] < lastEdge[0] ? 1 : -1; // Heading left (1) or right (-1)
		return scalar * (fx - 0.5) <= 0;
	}
};

module.exports = SquareGridGeometryCore;
